import { useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import JobTabs, { JobTag, projectTree } from '../components/JobTabs.js';
import { TagEditor } from '../components/TagChips.js';
import { addMoment, deleteMoment, getPerson, listPersonMoments, listProjects, updateMoment, updatePerson } from '../lifeos/api.js';
import { birthdayLabel, daysLeftLabel, daysToBirthday, humanize, since, stamp, ymdInHomeZone } from '../lifeos/format.js';
import FormSheet, { type Values } from '../lifeos/FormSheet.js';
import { text } from '../lifeos/forms.js';
import { upcomingHref } from '../lifeos/links.js';
import { channelLabel } from '../lifeos/timeline.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import Sheet from '../lifeos/Sheet.js';
import { TaskItem, useTaskActions } from '../lifeos/tasks.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Group, Icon, IconButton, Loader, MoreRow, Page, Row, Fact, TypeTile, When } from '../ui/index.js';
import type { Birthday, BirthdayInput, FamilyMember, Moment, Person as PersonT, PersonFact, PersonPatch, Project, Sphere } from '../lifeos/types.js';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const RELATIONSHIPS = ['friend', 'family', 'client', 'colleague', 'vendor', 'other'];

/** Stored birthday → the PUT input shape (`year` omitted when unknown). */
export function toBirthdayInput(b: Birthday | null | undefined): BirthdayInput | null {
  if (!b) return null;
  return b.year === null ? { month: b.month, day: b.day } : { month: b.month, day: b.day, year: b.year };
}

/** Month/day/year form strings → a birthday, null when all blank. Throws on half-filled input. */
export function parseBirthday(month: string | undefined, day: string | undefined, year: string | undefined): BirthdayInput | null {
  const m = Number(month);
  const d = Number(day);
  const y = text(year);
  if (!text(month) && !text(day) && !y) return null;
  if (!Number.isInteger(m) || m < 1 || m > 12 || !Number.isInteger(d) || d < 1 || d > 31) {
    throw new Error('Birthday: pick a month and a day.');
  }
  if (y === null) return { month: m, day: d };
  const yn = Number(y);
  if (!Number.isInteger(yn) || yn < 1900 || yn > 2100) throw new Error('Birthday: the year looks wrong.');
  return { month: m, day: d, year: yn };
}

/** The family list as the PUT expects it, keeping every member's birthday. */
export function familyInput(family: readonly FamilyMember[]): NonNullable<PersonPatch['family']> {
  return family.map((f) => ({ name: f.name, relation: f.relation, birthday: toBirthdayInput(f.birthday) }));
}

const birthdayFields = [
  {
    key: 'bMonth',
    label: 'Birthday month',
    kind: 'select' as const,
    options: [{ value: '', label: '—' }, ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))],
  },
  { key: 'bDay', label: 'Day', kind: 'number' as const },
  { key: 'bYear', label: 'Year', kind: 'number' as const, hint: 'optional' },
];

const birthdayValues = (b: Birthday | null | undefined): Values => ({
  bMonth: b ? String(b.month) : '',
  bDay: b ? String(b.day) : '',
  bYear: b?.year ? String(b.year) : '',
});

/** 'Oct 13, 2022' / 'Oct 13'. */
export function momentDate(b: Birthday): string {
  return `${MONTHS[b.month - 1]!.slice(0, 3)} ${b.day}${b.year ? `, ${b.year}` : ''}`;
}

const momentFields = [
  { key: 'label', label: 'What', required: true, wide: true, placeholder: 'e.g. Dad passed, sober since, got married' },
  {
    key: 'tone',
    label: 'Kind of day',
    kind: 'select' as const,
    options: [
      { value: 'hard', label: 'Hard day — check in' },
      { value: 'happy', label: 'Good day — celebrate' },
    ],
  },
  {
    key: 'bMonth',
    label: 'Month',
    kind: 'select' as const,
    options: [{ value: '', label: '—' }, ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))],
  },
  { key: 'bDay', label: 'Day', kind: 'number' as const },
  { key: 'bYear', label: 'Year', kind: 'number' as const, hint: 'optional — gives "4 years"' },
  { key: 'note', label: 'Note to self', kind: 'textarea' as const, placeholder: 'e.g. Call, don’t text' },
];

/** Messages shown before "Show N more". */
const MESSAGES_SHOWN = 3;

/**
 * A person (`/people/:id`): facts first (birthday, last talked, sphere), then
 * family, what to remember, open tasks, what's coming up and the last
 * messages. Birthdays are stored as calendar events server-side; this page
 * only ever sends {month, day, year?}. Removing is a swipe (or the × on a
 * pointer device), always with Undo.
 */
export default function Person(): JSX.Element {
  const { id = '' } = useParams();
  const detail = useLoad(() => getPerson(id), [id]);
  const spheres = useSpheres();
  const toast = useToast();
  const actions = useTaskActions(detail.reload);
  const [sheet, setSheet] = useState<'about' | 'family' | 'work' | null>(null);
  // Moments load separately: they are never part of the person detail (which MCP also reads).
  const moments = useLoad(() => listPersonMoments(id), [id]);
  const [momentSheet, setMomentSheet] = useState<Moment | 'new' | null>(null);
  const [fact, setFact] = useState('');
  const [allMessages, setAllMessages] = useState(false);
  // Tags flip at once on tap; the server copy replaces this after the reload.
  const [tagsNow, setTagsNow] = useState<string[] | null>(null);
  useEffect(() => setTagsNow(null), [detail.data]);
  const today = ymdInHomeZone(new Date());

  /** Save a patch now; Undo puts the previous values back. */
  async function save(p: PersonT, patch: PersonPatch, before: PersonPatch, msg: string): Promise<void> {
    try {
      await updatePerson(p.id, patch);
      toast.undoable({ text: msg, undo: () => updatePerson(p.id, before), onUndone: () => void detail.reload() });
      void detail.reload();
    } catch (err) {
      toast.error(`Couldn't save: ${errorText(err)}`);
    }
  }

  const back = { to: '/people', label: 'People' };
  const removeBtn = (label: string, onClick: () => void): JSX.Element => (
    <button type="button" className="la-menu la-remove" aria-label={label} onClick={onClick}>
      <Icon name="x" size={16} />
    </button>
  );

  if (!detail.data) {
    return (
      <Page title="" back={back}>
        <Loader state={detail}>{() => null}</Loader>
      </Page>
    );
  }
  const { person: p, orgs, recentMessages, tasks, upcoming } = detail.data;
  const linked = detail.data.projects ?? [];
  const job = p.sphereKey ? spheres.find((s) => s.key === p.sphereKey) : undefined;
  const openTasks = tasks.filter((t) => !actions.hidden.has(t.id));
  const now = new Date();
  const messages = allMessages ? recentMessages : recentMessages.slice(0, MESSAGES_SHOWN);

  return (
    <Page
      title={p.name}
      sub={(tagsNow ?? p.tags ?? []).length > 0 ? (tagsNow ?? p.tags ?? []).map(humanize).join(' · ') : p.relationship ? humanize(p.relationship) : undefined}
      back={back}
      actions={<IconButton icon="pencil" label="Edit" onClick={() => setSheet('about')} />}
    >
      <Group label="About">
        <Fact label="Birthday">{p.birthday ? birthdayLabel(p.birthday, today) : '—'}</Fact>
        <Fact label="Last talked">{since(p.lastContactAt, now)}</Fact>
        <Fact label="Job">{job ? <JobTag sphere={job} /> : p.sphereKey ?? 'None'}</Fact>
        {p.howMet && <Fact label="How we met">{p.howMet}</Fact>}
        {p.importance >= 3 && <Fact label="Inner circle">Yes</Fact>}
        {orgs.map((o) => (
          <Fact key={o.id} label={humanize(o.kind)} to={`/people?org=${encodeURIComponent(o.id)}`}>
            {o.name}
          </Fact>
        ))}
      </Group>

      <Group header="Tags" className="person__tags" footer="Tap to add or remove. Someone can be a friend and a client.">
        <li className="ui-swipe">
          <TagEditor
            tags={tagsNow ?? p.tags ?? []}
            onChange={(next) => {
              const before = p.tags ?? [];
              setTagsNow(next);
              updatePerson(p.id, { tags: next }).then(
                () => {
                  toast.undoable({ text: 'Tags saved', undo: () => updatePerson(p.id, { tags: before }), onUndone: () => void detail.reload() });
                  void detail.reload();
                },
                (err: unknown) => {
                  setTagsNow(null);
                  toast.error(`Couldn't save tags: ${errorText(err)}`);
                },
              );
            }}
          />
        </li>
      </Group>

      <Group header="Projects" action={{ label: 'Change', onClick: () => setSheet('work') }} className="person__projects">
        {linked.map((pr) => (
          <Row key={pr.id} to={`/projects/${pr.id}`} leading={<Icon name="folder" size={20} />} title={pr.name} />
        ))}
        {linked.length === 0 && <Row tone="muted" chevron={false} title="Not on any project — tap to add" onClick={() => setSheet('work')} />}
      </Group>

      <Group header="Dates that matter" action={{ label: 'Add', onClick: () => setMomentSheet('new') }} footer="Mo's Rule: you get a nudge to reach out on the day.">
        {(moments.data ?? [])
          .map((m) => ({ m, n: daysToBirthday(m.date, today) }))
          .sort((a, b) => a.n - b.n)
          .map(({ m, n }) => (
            <Row
              key={m.id}
              tone={m.tone === 'hard' ? 'default' : 'accent'}
              title={m.label}
              sub={[momentDate(m.date), m.tone === 'hard' ? 'hard day' : 'good day', m.note].filter(Boolean).join(' · ')}
              detail={n <= 30 ? daysLeftLabel(n) : undefined}
              onClick={() => setMomentSheet(m)}
            />
          ))}
        {moments.data && moments.data.length === 0 && (
          <Row tone="muted" chevron={false} title="Add a date that matters to them" onClick={() => setMomentSheet('new')} />
        )}
      </Group>

      <Group header="Family" action={p.family.length > 0 ? { label: 'Add', onClick: () => setSheet('family') } : undefined}>
        {p.family.length === 0 ? (
          <Row tone="accent" leading={<Icon name="plus" size={20} />} title="Add family" onClick={() => setSheet('family')} />
        ) : (
          p.family.map((f, i) => {
            const remove = (): void =>
              void save(p, { family: familyInput(p.family.filter((_, j) => j !== i)) }, { family: familyInput(p.family) }, `Removed ${f.name}`);
            return (
              <Row
                key={`${f.name}:${i}`}
                title={f.name}
                sub={
                  <>
                    {f.relation}
                    {f.birthday && ` · ${birthdayLabel(f.birthday, today)}`}
                  </>
                }
                trailing={removeBtn(`Remove ${f.name}`, remove)}
                swipe={{ trailing: [{ label: 'Remove', icon: 'trash', tone: 'bad', onAction: remove }] }}
              />
            );
          })
        )}
      </Group>

      <Group header="Remember">
        {p.facts.map((f, i) => {
          const remove = (): void => void save(p, { facts: p.facts.filter((_, j) => j !== i) }, { facts: p.facts }, 'Removed a fact');
          return (
            <Row
              key={`${f.text}:${i}`}
              title={f.text}
              sub={f.at || f.source ? [f.at ? stamp(f.at) : null, f.source].filter(Boolean).join(' · ') : undefined}
              trailing={removeBtn(`Remove fact ${f.text}`, remove)}
              swipe={{ trailing: [{ label: 'Remove', icon: 'trash', tone: 'bad', onAction: remove }] }}
            />
          );
        })}
        <li className="ui-swipe la-addrow">
          <form
            className="la-addrow__form"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              const t = fact.trim();
              if (!t) return;
              setFact('');
              const next: PersonFact[] = [...p.facts, { text: t, at: new Date().toISOString(), source: 'web' }];
              void save(p, { facts: next }, { facts: p.facts }, 'Fact added');
            }}
          >
            <Icon name="plus" size={20} className="la-addrow__icon" />
            <input placeholder="Remember something about them" aria-label="New fact" value={fact} onChange={(e) => setFact(e.target.value)} />
            {fact.trim() && (
              <button type="submit" className="la-addrow__save">
                Add
              </button>
            )}
          </form>
        </li>
      </Group>

      <Group header="Open tasks">
        {openTasks.map((t) => (
          <TaskItem key={t.id} task={t} today={today} color={sphereColor(spheres, t.sphereKey)} onToggle={() => actions.complete(t)} />
        ))}
      </Group>

      <Group header="Coming up">
        {upcoming.filter((u) => !(u.type === 'task' && openTasks.some((t) => String(t.id) === String(u.id)))).map((u) => (
          <Row
            key={`${u.type}:${u.id}:${u.dueOn}`}
            to={upcomingHref(u)}
            leading={<TypeTile type={u.type} />}
            title={u.title}
            sub={humanize(u.type)}
            detail={<When tone={u.daysLeft < 0 ? 'overdue' : u.daysLeft === 0 ? 'today' : 'later'}>{daysLeftLabel(u.daysLeft)}</When>}
          />
        ))}
      </Group>

      <Group header="Recent messages">
        {recentMessages.length > 0 && (
          [
            ...messages.map((m) => (
              <Row
                key={m.id}
                className={`la-msg la-msg--${m.direction}`}
                title={<span className="la-msg__text">{m.subject ? `${m.subject}\n${m.text}` : m.text}</span>}
                sub={`${m.direction === 'out' ? 'You' : p.name} · ${channelLabel(m.channel)} · ${stamp(m.sentAt)}`}
              />
            )),
            <MoreRow key="more" count={recentMessages.length - messages.length} onClick={() => setAllMessages(true)} />,
          ]
        )}
      </Group>

      {sheet === 'about' && (
        <FormSheet
          title={`Edit ${p.name}`}
          fields={[
            {
              key: 'relationship',
              label: 'Relationship',
              kind: 'select',
              options: [{ value: '', label: '—' }, ...RELATIONSHIPS.map((r) => ({ value: r, label: humanize(r) }))],
            },
            {
              key: 'importance',
              label: 'Importance',
              kind: 'select',
              options: [
                { value: '1', label: 'Low' },
                { value: '2', label: 'Normal' },
                { value: '3', label: 'Inner circle' },
              ],
            },
            {
              key: 'sphereKey',
              label: 'Job',
              kind: 'select',
              options: [{ value: '', label: 'None' }, ...spheres.map((s) => ({ value: s.key, label: s.name }))],
            },
            { key: 'howMet', label: 'How we met', wide: true },
            ...birthdayFields,
          ]}
          initial={{
            relationship: p.relationship && RELATIONSHIPS.includes(p.relationship) ? p.relationship : '',
            importance: String(p.importance),
            sphereKey: p.sphereKey ?? '',
            howMet: p.howMet ?? '',
            ...birthdayValues(p.birthday),
          }}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            await updatePerson(p.id, {
              relationship: text(v.relationship) ?? (p.relationship && !RELATIONSHIPS.includes(p.relationship) ? p.relationship : null),
              importance: Number(v.importance),
              sphereKey: v.sphereKey || null,
              howMet: text(v.howMet),
              birthday: parseBirthday(v.bMonth, v.bDay, v.bYear),
            });
            toast.info(`Saved ${p.name}`);
            void detail.reload();
          }}
        />
      )}
      {sheet === 'work' && (
        <JobProjectsSheet
          name={p.name}
          spheres={spheres}
          sphereKey={p.sphereKey}
          projectIds={linked.map((x) => x.id)}
          onClose={() => setSheet(null)}
          onSave={async (sphereKey, projectIds) => {
            await updatePerson(p.id, { sphereKey, projectIds });
            toast.info(`Saved ${p.name}`);
            void detail.reload();
          }}
        />
      )}
      {momentSheet && (
        <FormSheet
          title={momentSheet === 'new' ? `A date that matters to ${p.name}` : `Edit ${momentSheet.label}`}
          submitLabel={momentSheet === 'new' ? 'Add' : 'Save'}
          fields={momentFields}
          initial={
            momentSheet === 'new'
              ? { label: '', tone: 'hard', note: '', ...birthdayValues(null) }
              : { label: momentSheet.label, tone: momentSheet.tone, note: momentSheet.note ?? '', ...birthdayValues(momentSheet.date) }
          }
          danger={
            momentSheet === 'new'
              ? undefined
              : {
                  label: 'Remove',
                  onClick: () => {
                    const m = momentSheet;
                    setMomentSheet(null);
                    void deleteMoment(p.id, m.id)
                      .then(() => {
                        toast.info(`Removed ${m.label}`);
                        void moments.reload();
                      })
                      .catch((err: unknown) => toast.error(`Couldn't remove: ${errorText(err)}`));
                  },
                }
          }
          onClose={() => setMomentSheet(null)}
          onSubmit={async (v) => {
            const date = parseBirthday(v.bMonth, v.bDay, v.bYear);
            if (!date) throw new Error('Pick a month and a day.');
            const body = { label: text(v.label)!, tone: v.tone === 'happy' ? ('happy' as const) : ('hard' as const), date, note: text(v.note) ?? null };
            if (momentSheet === 'new') await addMoment(p.id, body);
            else await updateMoment(p.id, momentSheet.id, body);
            void moments.reload();
          }}
        />
      )}
      {sheet === 'family' && (
        <FormSheet
          title={`Add family for ${p.name}`}
          submitLabel="Add"
          fields={[
            { key: 'name', label: 'Name', required: true },
            { key: 'relation', label: 'Relation', required: true, placeholder: 'e.g. daughter' },
            ...birthdayFields,
          ]}
          initial={{ name: '', relation: '', ...birthdayValues(null) }}
          onClose={() => setSheet(null)}
          onSubmit={async (v) => {
            const member = {
              name: text(v.name)!,
              relation: text(v.relation)!,
              birthday: parseBirthday(v.bMonth, v.bDay, v.bYear),
            };
            await updatePerson(p.id, { family: [...familyInput(p.family), member] });
            toast.info(`Added ${member.name}`);
            void detail.reload();
          }}
        />
      )}
    </Page>
  );
}

/** Projects visible under a job: that job's (and their sub-projects), or all when no job. */
export function projectsForJob(projects: readonly Project[], sphereKey: string | null): Project[] {
  return projects.filter((p) => p.status !== 'archived' && (!sphereKey || p.sphereKey === sphereKey));
}

/**
 * Job + projects for a person in one sheet: big job buttons (None = no job),
 * then a tick list of projects narrowed to that job, sub-projects indented.
 * A ticked project from another job stays listed so it can be unticked.
 */
export function JobProjectsSheet({
  name,
  spheres,
  sphereKey,
  projectIds,
  onSave,
  onClose,
}: {
  name: string;
  spheres: readonly Sphere[];
  sphereKey: string | null;
  projectIds: readonly string[];
  onSave: (sphereKey: string | null, projectIds: string[]) => Promise<void>;
  onClose: () => void;
}): JSX.Element {
  const all = useLoad(() => listProjects(), []);
  const [jobKey, setJobKey] = useState<string | null>(sphereKey);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(projectIds));
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const shown = projectTree(
    (all.data ?? []).filter((pr) => picked.has(pr.id) || projectsForJob([pr], jobKey).length > 0),
  );
  const toggle = (id: string): void =>
    setPicked((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const save = async (): Promise<void> => {
    setBusy(true);
    setErr(null);
    try {
      await onSave(jobKey, [...picked]);
      onClose();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };
  return (
    <Sheet title={`Job & projects for ${name}`} onClose={onClose} action={{ label: 'Save', onClick: () => void save(), disabled: busy }}>
      <p className="person__label">Job</p>
      <JobTabs spheres={spheres} value={jobKey} onChange={setJobKey} label="Job" allLabel="None" />
      <p className="person__label">Projects{jobKey ? ` in ${spheres.find((s) => s.key === jobKey)?.name ?? jobKey}` : ''}</p>
      <Loader state={all}>
        {() =>
          shown.length === 0 ? (
            <p className="lo-sheet__hint">No projects{jobKey ? ' in this job' : ''} yet.</p>
          ) : (
            <ul className="ui-group ui-group--flush" aria-label="Projects">
              {shown.map(({ project: pr, depth }) => (
                <li key={pr.id} className="ui-swipe">
                  <label className={`person__pick${depth > 0 ? ' person__pick--sub' : ''}`} style={{ paddingLeft: 16 + depth * 20 }}>
                    <input type="checkbox" checked={picked.has(pr.id)} onChange={() => toggle(pr.id)} aria-label={pr.name} />
                    <span className="ui-dot" style={{ background: sphereColor(spheres, pr.sphereKey) }} aria-hidden="true" />
                    <span>{pr.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )
        }
      </Loader>
      {err && <p className="lo-form__error" role="alert">{err}</p>}
    </Sheet>
  );
}
